import React from 'react';
import { format } from 'date-fns';
import DailyTile, { MealInfo } from '@/components/ui/daily-tile';
import { MealPlan } from '@/types/mealPlan';
import { MealNote } from '@/types/mealNote';
import { Recipe } from '@/types/recipe';
import { MealCompositionWithRecipes } from '@/types/mealComposition';

interface MonthlyMealTileProps {
  date: Date;
  mealPlans: MealPlan[];
  mealNotes: MealNote[];
  recipes: Recipe[];
  compositions: MealCompositionWithRecipes[];
  isLoading: boolean;
  isCurrentMonth: boolean;
}

const MonthlyMealTile: React.FC<MonthlyMealTileProps> = ({
  date,
  mealPlans,
  mealNotes,
  recipes,
  compositions,
  isLoading,
  isCurrentMonth,
}) => {

  // Convert meal plans to MealInfo format
  const mealPlanMeals: MealInfo[] = mealPlans.map(mealPlan => {
    if (mealPlan.recipeId) {
      const recipe = recipes.find(r => r.id === mealPlan.recipeId);
      if (recipe) {
        return {
          type: 'recipe' as const,
          id: recipe.id,
          name: recipe.title,
          isQuickRecipe: recipe.isQuickRecipe,
          prepTime: recipe.prepTime,
          cookTime: recipe.cookTime,
          mealType: mealPlan.mealType,
          authorFirstName: mealPlan.authorFirstName
        };
      }
    }
    
    if (mealPlan.mealCompositionId) {
      const composition = compositions.find(c => c.id === mealPlan.mealCompositionId);
      if (composition) {
        return {
          type: 'composition' as const,
          id: composition.id,
          name: composition.name,
          recipeCount: composition.recipes.length,
          mealType: mealPlan.mealType,
          authorFirstName: mealPlan.authorFirstName
        };
      }
    }
    
    return null;
  }).filter(Boolean) as MealInfo[];

  // Convert meal notes to MealInfo format
  const mealNoteMeals: MealInfo[] = mealNotes.map(mealNote => ({
    type: 'note' as const,
    id: mealNote.id,
    name: mealNote.noteText,
    mealType: mealNote.mealType as 'lunch' | 'dinner',
    authorFirstName: mealNote.authorFirstName
  }));

  // Combine meals
  const meals: MealInfo[] = [...mealPlanMeals, ...mealNoteMeals];

  return (
    <div className={`${!isCurrentMonth ? 'opacity-50' : ''}`}>
      <DailyTile
        date={date}
        meals={meals}
        isLoading={isLoading}
        showTodayBadge={false}
        className="text-xs"
        variant={isCurrentMonth ? 'default' : 'past'}
        showAllMealTypes={true}
        heightMode="flexible"
      />
    </div>
  );
};

export default MonthlyMealTile;